// Local times are represented as "local milliseconds": the wall-clock time encoded as if it were UTC.

const MINUTE_MS = 60_000;
export const DAY_MS = 24 * 60 * MINUTE_MS;

/** Offset from UTC in minutes at an instant. */
export type OffsetAt = (instant: number) => number;

// Java's ZoneId also accepts fixed offsets with a UTC/GMT/UT prefix, which Intl does not.
const FIXED_OFFSET = /^(?:UTC|GMT|UT)?([+-])(\d{1,2})(?::?(\d{2}))?$/;

function fixedOffset(zoneId: string): number | null {
  if (zoneId === 'Z') return 0;
  const match = FIXED_OFFSET.exec(zoneId);
  if (!match) return null;
  const minutes = Number(match[2]) * 60 + Number(match[3] ?? 0);
  return match[1] === '-' ? -minutes : minutes;
}

/** Null when this browser cannot compute the zone's offsets. */
export function zoneOffsets(zoneId: string): OffsetAt | null {
  const fixed = fixedOffset(zoneId);
  if (fixed != null) return () => fixed;
  let format: Intl.DateTimeFormat;
  try {
    format = new Intl.DateTimeFormat('en-US', {
      timeZone: zoneId,
      hourCycle: 'h23',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
    });
  } catch {
    return null;
  }
  return (instant) => {
    const parts = format.formatToParts(instant);
    const part = (type: string) => Number(parts.find((p) => p.type === type)?.value);
    const local = Date.UTC(
      part('year'),
      part('month') - 1,
      part('day'),
      part('hour'),
      part('minute')
    );
    return Math.round((local - Math.floor(instant / MINUTE_MS) * MINUTE_MS) / MINUTE_MS);
  };
}

export function toLocal(instant: number, offsetAt: OffsetAt): number {
  return instant + offsetAt(instant) * MINUTE_MS;
}

/**
 * The instant of a local time, resolved like Java's `ZonedDateTime.of`: in an overlap the earlier instant, in a gap
 * the local time moved forward by the gap. Assumes at most one offset change within a day of it.
 */
export function toInstant(local: number, offsetAt: OffsetAt): number {
  const before = offsetAt(local - DAY_MS);
  const after = offsetAt(local + DAY_MS);
  const early = local - before * MINUTE_MS;
  const late = local - after * MINUTE_MS;
  const earlyValid = offsetAt(early) === before;
  const lateValid = offsetAt(late) === after;
  if (earlyValid && lateValid) return Math.min(early, late);
  if (lateValid) return late;
  return early;
}

export function startOfLocalDay(local: number): number {
  return Math.floor(local / DAY_MS) * DAY_MS;
}

/** 0 for Monday. */
export function localWeekday(local: number): number {
  return (new Date(local).getUTCDay() + 6) % 7;
}
